/**
 * Maranello Luce Design - Chart helpers and shared chart utilities
 */
import { cssVar } from './core/utils';
import { escapeHtml } from './core/sanitize';

function getDpr(): number {
  return typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
}

/** Build the default chart color series from CSS custom properties. */
export function buildSeries(): string[] {
  return [
    cssVar('--mn-accent', 'var(--mn-accent)'),
    cssVar('--signal-danger', 'var(--signal-danger)'),
    cssVar('--signal-ok', 'var(--signal-ok)'),
    cssVar('--mn-warning', 'var(--mn-warning)'),
    cssVar('--mn-info', 'var(--mn-info)'),
    cssVar('--mn-border-strong', 'var(--mn-border-strong)'),
    cssVar('--mn-error', 'var(--mn-error)'),
    cssVar('--mn-success', 'var(--mn-success)'),
    cssVar('--signal-warning', 'var(--signal-warning)'),
    cssVar('--signal-info', 'var(--signal-info)'),
    cssVar('--mn-text-tertiary', 'var(--mn-text-tertiary)'),
    cssVar('--mn-accent-hover', 'var(--mn-accent-hover)'),
  ];
}

export function getSERIES(): string[] { return buildSeries(); }

/** Set up a hi-DPI canvas and return its 2D context, or null if unavailable. */
export function chartHiDpi(
  canvas: HTMLCanvasElement, w: number, h: number,
): CanvasRenderingContext2D | null {
  const dpr = getDpr();
  const cw = Math.max(w, 20);
  const ch = Math.max(h, 20);
  canvas.width = cw * dpr;
  canvas.height = ch * dpr;
  canvas.style.width = cw + 'px';
  canvas.style.height = ch + 'px';
  const ctx = canvas.getContext('2d');
  if (!ctx) { console.warn('[Maranello] chartHiDpi: 2D context unavailable'); return null; }
  ctx.scale(dpr, dpr);
  return ctx;
}

/** Get usable dimensions from canvas parent or data attributes. */
export function getCanvasSize(
  canvas: HTMLCanvasElement, defaultW = 200, defaultH = 100,
): { width: number; height: number } {
  const dw = parseInt(canvas.getAttribute('data-width') ?? '', 10);
  const dh = parseInt(canvas.getAttribute('data-height') ?? '', 10);
  if (dw > 0 && dh > 0) return { width: dw, height: dh };

  const aw = parseInt(canvas.getAttribute('width') ?? '', 10);
  const ah = parseInt(canvas.getAttribute('height') ?? '', 10);
  if (aw > 0 && ah > 0) return { width: aw, height: ah };

  if (canvas.parentElement) {
    const rect = canvas.parentElement.getBoundingClientRect();
    if (rect.width > 10 && rect.height > 10) {
      return { width: rect.width, height: rect.height };
    }
  }

  return { width: defaultW, height: defaultH };
}

/** Parse a hex color to rgba string with given alpha. */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Create a vertical gradient from a hex color (top alpha to bottom 0). */
export function hexFillGradient(
  ctx: CanvasRenderingContext2D, hex: string, h: number, opacity: number,
): CanvasGradient {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, `rgba(${r},${g},${b},${opacity})`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  return grad;
}

/** Data row for sr-only table generated by applyChartA11y. */
export interface A11yDataRow { label: string; value: string | number }

/** Apply accessibility attributes to a canvas chart element.
 *  If data rows provided, injects an sr-only table for screen readers. */
export function applyChartA11y(
  canvas: HTMLCanvasElement, label: string, data?: A11yDataRow[],
): void {
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', label);
  canvas.textContent = label;
  if (!canvas.parentElement) return;

  // Reuse or create sr-only sibling
  let srEl = canvas.nextElementSibling;
  if (!srEl || !srEl.classList.contains('mn-sr-only')) {
    srEl = document.createElement('span');
    srEl.className = 'mn-sr-only';
    canvas.parentElement.insertBefore(srEl, canvas.nextSibling);
  }

  if (data && data.length > 0) {
    const rows = data.map(
      (r) => `<tr><td>${escapeHtml(String(r.label))}</td><td>${escapeHtml(String(r.value))}</td></tr>`,
    ).join('');
    srEl.innerHTML =
      `<table><caption>${escapeHtml(label)}</caption><tbody>${rows}</tbody></table>`;
  } else {
    srEl.textContent = label;
  }
}

/** Draw a smooth bezier curve through data points. */
export function drawSmoothLine(
  ctx: CanvasRenderingContext2D,
  data: number[],
  getX: (i: number) => number,
  getY: (v: number) => number,
  smooth: boolean,
): void {
  ctx.moveTo(getX(0), getY(data[0]));
  if (smooth && data.length > 2) {
    for (let i = 1; i < data.length; i++) {
      const cpx = (getX(i - 1) + getX(i)) / 2;
      ctx.bezierCurveTo(cpx, getY(data[i - 1]), cpx, getY(data[i]), getX(i), getY(data[i]));
    }
  } else {
    for (let i = 1; i < data.length; i++) {
      ctx.lineTo(getX(i), getY(data[i]));
    }
  }
}
